import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    SvgIcon,
    TextField,
    Typography,
} from '@mui/material';
import PlusIcon from '@heroicons/react/24/solid/PlusIcon';
import InformationCircleIcon from '@heroicons/react/24/solid/InformationCircleIcon';
import MagnifyingGlassIcon from '@heroicons/react/24/solid/MagnifyingGlassIcon';
import { FunctionComponent, ReactNode, useState } from 'react';
import { WidgetManifestEntry } from '@/models/widget';
import { APP_BAR_HEIGHT_VAR } from '@/layouts/fullScreen/FullScreenLayout';

interface WidgetListCardProps {
    widgets: WidgetManifestEntry[];
    isLoading: boolean;
    isError: boolean;
    /** Re-runs the manifest query after a failed load. */
    onRetry: () => void;
    onAddWidget: (widget: WidgetManifestEntry) => void;
    /** `widget` ids already placed in the preview; their rows are greyed. */
    addedWidgetIds: string[];
    /** Opens the documentation dialog for a single widget. */
    onShowDocs: (widget: WidgetManifestEntry) => void;
    /** Rendered in the header beside the title: the list below belongs to the chosen version. */
    versionSelect?: ReactNode;
}

/**
 * Gap between the sticky app bar and the pinned card, matching the page's own
 * vertical padding. `64px` fallback covers a first paint before the layout has
 * measured the bar.
 */
const STICKY_OFFSET = `calc(var(${APP_BAR_HEIGHT_VAR}, 64px) + 32px)`;

/**
 * The rail listing every available widget; a click adds one.
 *
 * On `md` and up it sticks below the app bar while the (much taller) right
 * column scrolls. The card is content-height — the parent grid sets
 * `alignItems: flex-start` — so the sticky box has room to travel inside its
 * grid area. If the list itself outgrows the viewport it scrolls internally
 * instead of having its bottom entries pushed out of reach.
 */
export const WidgetListCard: FunctionComponent<WidgetListCardProps> = ({
    widgets,
    isLoading,
    isError,
    onRetry,
    onAddWidget,
    addedWidgetIds,
    onShowDocs,
    versionSelect,
}) => {
    const [search, setSearch] = useState('');
    const query = search.trim().toLowerCase();
    const visibleWidgets = widgets.filter(
        (widget) =>
            widget.label.de.toLowerCase().includes(query) ||
            widget.tag.includes(query)
    );

    return (
        <Card
            sx={{
                position: { md: 'sticky' },
                top: { md: STICKY_OFFSET },
                maxHeight: { md: `calc(100vh - ${STICKY_OFFSET} - 32px)` },
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <CardContent sx={{ flexShrink: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant='h6'>Widgets</Typography>
                    <Box
                        sx={{
                            width: 100,
                        }}
                    >
                        {versionSelect}
                    </Box>
                </Box>
            </CardContent>
            <Divider sx={{ width: '100%', flexShrink: 0 }} />

            {!isLoading && !isError && widgets.length > 0 && (
                <Box sx={{ px: 4, pt: 4, pb: 2, flexShrink: 0 }}>
                    <TextField
                        fullWidth
                        label='Widgets durchsuchen'
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SvgIcon fontSize='small'>
                                            <MagnifyingGlassIcon />
                                        </SvgIcon>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Box>
            )}

            {isLoading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 4,
                    }}
                >
                    <CircularProgress size={24} />
                </Box>
            ) : isError ? (
                <Box sx={{ p: 2 }}>
                    <Typography color='error' variant='body2' sx={{ mb: 2 }}>
                        Widgets konnten nicht geladen werden.
                    </Typography>
                    <Button size='small' variant='outlined' onClick={onRetry}>
                        Erneut versuchen
                    </Button>
                </Box>
            ) : widgets.length === 0 ? (
                <Box sx={{ p: 2 }}>
                    <Typography color='text.secondary' variant='body2'>
                        Keine Widgets verfügbar.
                    </Typography>
                </Box>
            ) : visibleWidgets.length === 0 ? (
                <Box sx={{ p: 2 }}>
                    <Typography color='text.secondary' variant='body2'>
                        Keine Widgets gefunden.
                    </Typography>
                </Box>
            ) : (
                <List disablePadding sx={{ overflowY: 'auto' }}>
                    {visibleWidgets.map((widget) => {
                        const label = widget.label.de;
                        const isAdded = addedWidgetIds.includes(widget.widget);

                        return (
                            /*
                             * Both icons are the list item's `secondaryAction`, i.e.
                             * siblings of the row button rather than children of it:
                             * a button nested inside a button is invalid markup and
                             * would make a click on an icon also add the widget.
                             */
                            <ListItem
                                key={widget.tag}
                                disablePadding
                                secondaryAction={
                                    <Stack direction='row' spacing={0.5}>
                                        <IconButton
                                            size='small'
                                            aria-label={`Dokumentation zu ${label}`}
                                            onClick={() => onShowDocs(widget)}
                                        >
                                            <SvgIcon fontSize='small'>
                                                <InformationCircleIcon />
                                            </SvgIcon>
                                        </IconButton>
                                        <IconButton
                                            size='small'
                                            aria-label={`${label} hinzufügen`}
                                            onClick={() => onAddWidget(widget)}
                                        >
                                            <SvgIcon fontSize='small'>
                                                <PlusIcon />
                                            </SvgIcon>
                                        </IconButton>
                                    </Stack>
                                }
                            >
                                {/*
                                 * Only rows not yet on the page highlight on
                                 * hover: that is where a click is the expected
                                 * next step. An added row still adds another
                                 * instance, but stays quiet like its greyed text.
                                 */}
                                <ListItemButton
                                    onClick={() => onAddWidget(widget)}
                                    // Room for the secondary action, which overlays
                                    // the row's own right edge.
                                    sx={{
                                        pr: 11,
                                        ...(isAdded && {
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                            },
                                        }),
                                    }}
                                >
                                    {/* Greyed, not disabled: a widget can be added more than once. */}
                                    <ListItemText
                                        primary={label}
                                        secondary={widget.tag}
                                        sx={{ opacity: isAdded ? 0.5 : 1 }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </Card>
    );
};
